import { useEffect } from 'react'
import { useRouteError, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { PageLayout } from '@/components/layout/PageLayout'

/**
 * RouteErrorFallback — errorElement for routes rendered inside AppLayout.
 *
 * Catches render errors of the active page (React Router acts as the error
 * boundary), so the sidebar/header stay usable and the user can recover by
 * navigating away or reloading. Rendered in place of the page inside the
 * layout's Outlet.
 */
export function RouteErrorFallback() {
  const error = useRouteError()
  const { t } = useTranslation('common')

  // React Router acts as the error boundary and swallows the error, so Sentry's
  // global handlers never see it — report it explicitly from here. The guard is
  // for thrown falsy values (`throw undefined`), which would otherwise become a
  // useless "Non-Error exception captured" issue.
  useEffect(() => {
    if (error) {
      Sentry.captureException(error)
    }
  }, [error])

  return (
    <PageLayout>
      <div className="flex flex-col items-center justify-center gap-4 h-full text-center">
        <AlertTriangle className="size-12 text-destructive" />
        <div className="space-y-1">
          <h1 className="text-xl font-semibold">{t('errorBoundary.title')}</h1>
          <p className="text-muted-foreground">{t('errorBoundary.description')}</p>
        </div>
        {import.meta.env.DEV && error instanceof Error && (
          <pre className="max-w-2xl overflow-auto rounded-md bg-muted p-4 text-left text-xs text-destructive">
            {error.stack ?? error.message}
          </pre>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            {t('errorBoundary.reload')}
          </Button>
          <Button asChild>
            <Link to="/">{t('errorBoundary.goHome')}</Link>
          </Button>
        </div>
      </div>
    </PageLayout>
  )
}
